import { useMemo } from 'react';
import { ROUTES_CONFIG } from '@/config/routesConfig';
import { RouteConfig } from '@/types/route';
import { useQuery } from '@tanstack/react-query';
import { getMenuList } from '@/api/menu';
import { buildRoutesFromMenu } from '@/utils/menuUtils';

/**
 * 获取不需要权限检查的路由
 */
const getNoPermissionRoutes = (routes: RouteConfig[]): RouteConfig[] => {
  return routes.filter((route) => {
    // 始终允许不需要权限的路由
    if (route.noPermissionRequired) return true;

    // 始终允许登录相关路由
    if (
      route.hideInMenu &&
      (route.path.includes('/login') || route.path.includes('/callback'))
    )
      return true;

    return false;
  });
};

/**
 * 处理路由权限的 hook
 */
export const usePermissionRoutes = (): {
  routes: RouteConfig[];
  loading: boolean;
} => {
  const {
    data: menuResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['menuList'],
    queryFn: getMenuList,
    retry: 3,
    retryDelay: 5000,
  });

  return useMemo(() => {
    // 获取所有不需要权限的路由
    const noPermissionRoutes = getNoPermissionRoutes(ROUTES_CONFIG);
    // 如果有错误或没有数据，只返回不需要权限的路由
    if (error || !menuResponse?.data) {
      console.warn(
        'Failed to load menu permissions, only allowing no-permission routes',
        error
      );
      return {
        routes: noPermissionRoutes,
        loading: isLoading,
      };
    }

    // 根据后端菜单数据构建有权限的路由配置
    const permissionRoutes = buildRoutesFromMenu(menuResponse.data);

    // 合并不需要权限的路由和有权限的路由
    return {
      routes: [...noPermissionRoutes, ...permissionRoutes],
      loading: isLoading,
    };
  }, [menuResponse, isLoading, error]);
};
